import React, { useState, useEffect, useRef, useCallback } from "react";
import { useAppState, actions } from "../../../contexts/AppStateContext";
import { useAppConfig } from "../../../AppConfigProvider";
import { useFocusEffect, useRoute } from "@react-navigation/native";
import {
  useMeQuery,
  StoragePathType,
  useProcessAttachmentMutation,
  useGetFileQuery,
} from "../../../graphql/generated/graphql";
import ChatBar from "../../../components/navigation/ChatBar/ChatBar";
import UpgradeModalContainer from "../../general/UpgradeModalContainer/UpgradeModalContainer";
import useAudioRecorder from "../../../hooks/useAudioRecorder";
import { useChatBar } from "../../../contexts/ChatBarContext";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { usePostHog } from "posthog-js/react";
import useWebSocket from "../../../hooks/useWebSocket";
import { useChatBarInput } from "../../../contexts/CharBarInputContext";
import Sentry from "../../../utils/sentry";
import { View } from "react-native";
import useFileUpload, { ProcessedFile } from "../../../hooks/useFileUpload";

const viewsWithoutAttachments = ["Onboarding", "Custom Resource Student"];

const viewsWithoutChat = ["Custom Course AITutor"];

function ChatBarContainer({ onSubmit = () => {} }) {
  const { companionName } = useAppConfig();
  const appConfig = useAppConfig();
  const isAxioOffering = appConfig.isAxioOffering;
  const posthog = usePostHog();

  const { state, handleAudioOffPress, handleAudioOnPress, dispatch } = useAppState();
  const { setShowChat, showChat, state: chatBarState, setMetaIsLoading, setSubmitEnabled } = useChatBar();
  const { setInputValue, inputValue } = useChatBarInput();
  const { sendMessage } = useWebSocket();

  const route = useRoute();

  const { data } = useMeQuery();
  const {
    data: fileData,
    startPolling,
    stopPolling,
    refetch,
  } = useGetFileQuery({ variables: { fileId: "" }, fetchPolicy: "network-only" });

  const chatInputRef = useRef(null);

  const { recording, toggleRecording } = useAudioRecorder(onSubmit);

  const [isUpgradeModalVisible, setIsUpgradeModalVisible] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const [isAttachmentLoading, setIsAttachmentLoading] = useState(false);

  const [processAttachment] = useProcessAttachmentMutation();

  const isAttachmentEnabled = !viewsWithoutAttachments.includes(route.name);

  const chatWindowDisabled = viewsWithoutChat.includes(route.name);

  useFocusEffect(
    useCallback(() => {
      // Function to execute when the screen is focused
      const handleDocumentKeyPress = () => {
        chatInputRef.current?.focus();
      };

      if (state.meta.keypressIsListening) {
        // Add the event listener only if input is not focused and the modal is not open
        document.addEventListener("keypress", handleDocumentKeyPress);
      } else {
        // Remove the event listener if input is focused or the modal is open
        document.removeEventListener("keypress", handleDocumentKeyPress);
      }

      // Cleanup the event listener when the component is unmounted
      return () => {
        document.removeEventListener("keypress", handleDocumentKeyPress);
      };
    }, [state.meta.keypressIsListening])
  );

  useEffect(() => {
    if (fileData?.getFile?.data?.length > 0) {
      const file = fileData.getFile.data[0];
      const isLoading = file.status !== "completed";
      if (!isLoading) {
        setIsAttachmentLoading(false);
        stopPolling();
        setSubmitEnabled(true);
      }
    }
  }, [fileData, setIsAttachmentLoading, stopPolling]);

  const setAudioOn = () => {
    if (!data?.me?.isPro) {
      setIsUpgradeModalVisible(true);
    } else {
      handleAudioOnPress();
    }
  };

  const handleRecordingStopPress = () => {
    toggleRecording();
  };

  const handleRecordingStartPress = () => {
    if (!data?.me?.isPro) {
      setIsUpgradeModalVisible(true);
    } else {
      toggleRecording();
    }
  };

  const handleCloseUpgradeModals = () => {
    setIsUpgradeModalVisible(false);
  };

  const handleToggleChat = () => {
    setShowChat(!showChat);
  };

  const handleQuickOptionPress = (value) => {
    handleSubmitEditing(value);
  };

  const handleSubmit = () => {
    handleSubmitEditing(undefined);
    onSubmit();
  };

  const handleChangeText = (text) => {
    setInputValue(text);
  };

  const handleBeforeUpload = () => {
    setIsAttachmentLoading(true);
    setSubmitEnabled(false);
  };

  const handleAfterUpload = async (processedFilesArray: ProcessedFile[]) => {
    for (const processedFile of processedFilesArray) {
      await processAttachment({
        variables: {
          fileId: processedFile.file.id,
        },
      });
      // Add the new file to the files array
      setAttachment({
        title: processedFile.file.fileName,
        fileType: processedFile.mimeType,
        id: processedFile.file.id,
        status: "pending",
      });
      refetch({ fileId: processedFile.file.id });
    }

    startPolling(3000);
  };

  const handleFileUploadError = () => {
    setIsAttachmentLoading(false);
    setSubmitEnabled(true);
    setAttachment(null);
  };

  const { handleFileUpload } = useFileUpload({
    storagePath: StoragePathType.ChatAttachments,
    beforeUpload: handleBeforeUpload,
    afterUpload: handleAfterUpload,
    onError: handleFileUploadError,
    multiple: true,
  });

  const handleDeleteAttachment = () => {
    setAttachment(null);
  };

  const handleSubmitEditing = async (quickItemValue) => {
    try {
      if (isAttachmentLoading === true) return;

      posthog?.capture("Message sent to axio");
      setAttachment(null);

      if (state.meta.isAdventure) {
        dispatch({
          type: actions.SET_META,
          payload: { isAdventure: false },
        });
      }

      await sendMessageToAxio(quickItemValue);

      if (!chatWindowDisabled) {
        setShowChat(true);
      }

      setInputValue("");
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const sendMessageToAxio = async (quickItemValue) => {
    const token = await AsyncStorage.getItem("accessToken");

    setMetaIsLoading(true);
    sendMessage({
      ...chatBarState.llmMessage,
      meta: {
        ...chatBarState.llmMessage.meta,
        token,
        isLoading: true,
        attachmentId: attachment ? attachment.id : false,
      },
      content: { ...chatBarState.llmMessage.content, message: quickItemValue || inputValue },
      state: {
        ...chatBarState.llmMessage.state,
      },
    });
  };

  return (
    <View
      style={{
        width: "100%",
        marginHorizontal: "auto",
        display: "flex",
        justifyContent: "center",
        flexDirection: "row",
      }}>
      <ChatBar
        value={inputValue}
        isRecording={recording}
        isAudioOn={state.meta.isAudioOn}
        placeholder={recording ? `${companionName} is listening...` : `Chat with ${companionName}...`}
        inputRef={chatInputRef}
        onChangeText={handleChangeText}
        onSubmitEditing={handleSubmit}
        onAudioOffPress={handleAudioOffPress}
        onAudioOnPress={setAudioOn}
        onRecordingStopPress={handleRecordingStopPress}
        onRecordingStartPress={handleRecordingStartPress}
        onToggleChatPress={handleToggleChat}
        onQuickOptionPress={handleQuickOptionPress}
        onAttachmentPress={handleFileUpload}
        onDeleteAttachmentPress={handleDeleteAttachment}
        isCustomOffering={isAxioOffering}
        showQuickOptions={true}
        showChat={showChat}
        isSubmitEnabled={chatBarState.isSubmitEnabled}
        isAttachmentEnabled={isAttachmentEnabled}
        attachment={attachment}
        isAttachmentLoading={isAttachmentLoading}
        chatWindowDisabled={chatWindowDisabled}
      />

      <UpgradeModalContainer visible={isUpgradeModalVisible} onClose={handleCloseUpgradeModals} />
    </View>
  );
}

export default ChatBarContainer;
